import { useCallback, useEffect, useState } from "react";

// Live-clock + refresh state shared across the app.
// POC: refresh just bumps a tick counter so jitter() produces fresh values.
// Production: refresh would re-fetch from /api/snapshot or the JSON build,
//   and the tick would correspond to the data vintage, not synthetic noise.

export interface LiveClock {
  tick: number;          // increments on every refresh
  refreshAt: number;     // ms epoch of the latest refresh
  nowMs: number;         // updates every second to drive "Xs ago"
  secondsAgo: number;
  autoRefreshSec: number;
  isRefreshing: boolean;
  refresh: () => void;
}

export function useLiveClock(autoRefreshMs = 30_000): LiveClock {
  const [tick, setTick] = useState(0);
  const [refreshAt, setRefreshAt] = useState(() => Date.now());
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Drive a 1Hz clock so the topbar counter updates each second.
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const refresh = useCallback(() => {
    setIsRefreshing(true);
    // Fake a brief network round-trip so the spinner is visible.
    setTimeout(() => {
      setTick((t) => t + 1);
      setRefreshAt(Date.now());
      setIsRefreshing(false);
    }, 350);
  }, []);

  // Auto-refresh on a cadence.
  useEffect(() => {
    const id = setInterval(refresh, autoRefreshMs);
    return () => clearInterval(id);
  }, [autoRefreshMs, refresh]);

  const secondsAgo = Math.max(0, Math.floor((nowMs - refreshAt) / 1000));

  return {
    tick,
    refreshAt,
    nowMs,
    secondsAgo,
    autoRefreshSec: Math.round(autoRefreshMs / 1000),
    isRefreshing,
    refresh,
  };
}

// Deterministic small perturbation around a base value.
// `magnitude` is the maximum fractional swing (e.g. 0.005 = ±0.5%).
// Pass `tick` (or `tick * 7 + offset` to vary phase across KPIs) as `seed`.
export function jitter(base: number, magnitude: number, seed: number): number {
  // simple deterministic hash → fractional [0, 1)
  const x = Math.sin(seed * 12.9898 + 78.233) * 43758.5453;
  const fract = x - Math.floor(x);
  return base * (1 + (fract - 0.5) * magnitude * 2);
}
